import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DescriptionIcon from '@material-ui/icons/Description';
import FolderIcon from '@material-ui/icons/Folder';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  Page,
  Header,
  Content,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
  alertApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GitlabCIApiRef } from '@immobiliarelabs/backstage-plugin-gitlab';
import { Link } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';

// ── Constants ─────────────────────────────────────────────────────────────────

const FONT = "'Arial', sans-serif";

// Category tag used to scope docs-instance uploads inside the persistence store
const DOCS_INSTANCE_CATEGORY = 'docs-instance';

const PIPELINE_STATUS_COLOR: Record<string, string> = {
  success: '#018136',
  failed: '#D72339',
  running: '#007EB1',
  pending: '#E96401',
  canceled: '#6b7280',
  skipped: '#6b7280',
  created: '#9ca3af',
  manual: '#E96401',
  scheduled: '#9ca3af',
};

const getPipelineColor = (status: string) =>
  PIPELINE_STATUS_COLOR[status] ?? '#9ca3af';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CatalogEntity {
  metadata: {
    name: string;
    title?: string;
    description?: string;
    annotations?: Record<string, string>;
    tags?: string[];
    namespace?: string;
  };
  kind: string;
  spec?: {
    type?: string;
    lifecycle?: string;
    owner?: string;
  };
}

interface UploadedDocArtifact {
  id: string;
  name: string;
  description?: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string;
  category?: string;
}

interface OpenUploadedDocInput {
  id: string;
  name: string;
}

interface PipelineInfo {
  status: string;
  ref: string;
  web_url: string;
  id: number;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  tabsBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: '24px',
  },
  sectionLabel: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    margin: '0 0 12px 0',
  },
  sectionDivider: {
    margin: '28px 0 20px',
  },

  /* ── Doc upload card ── */
  createCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    background: theme.palette.background.paper,
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '24px',
  },
  createCardText: {
    flex: 1,
  },
  createCardTitle: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '15px',
    lineHeight: '22px',
    color: theme.palette.text.primary,
    margin: 0,
  },
  createCardSub: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
    margin: '2px 0 0',
  },

  /* ── Doc list item ── */
  docRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    background: theme.palette.background.paper,
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '8px',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  docIcon: {
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  docInfo: {
    flex: 1,
    minWidth: 0,
  },
  docName: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    margin: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  docMeta: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.secondary,
    margin: '2px 0 0',
  },
  docActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },

  /* ── Catalog entity card ── */
  entityCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '16px',
    background: theme.palette.background.paper,
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    borderLeft: '3px solid transparent',
    height: '100%',
    minHeight: '180px',
    boxSizing: 'border-box' as const,
    gap: '6px',
  },
  entityCardTitle: {
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.text.primary,
    margin: 0,
    wordBreak: 'break-word' as const,
  },
  entityCardDesc: {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '18px',
    color: theme.palette.text.secondary,
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    '-webkit-line-clamp': 5,
    '-webkit-box-orient': 'vertical',
  } as any,
  entityCardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '4px',
  },
  chip: {
    height: '20px',
    fontSize: '11px',
    fontFamily: FONT,
    fontWeight: 600,
    borderRadius: '10px',
  },

  /* ── Pipeline status dot (repo tab) ── */
  statusDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
    marginRight: '6px',
  },
  loadingRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 0',
  },
}));

// ── Helper ─────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getEntityCatalogPath(e: CatalogEntity) {
  const ns = e.metadata.namespace ?? 'default';
  return `/catalog/${ns}/${e.kind.toLowerCase()}/${e.metadata.name}`;
}

function getEntityDocsPath(e: CatalogEntity) {
  const ns = e.metadata.namespace ?? 'default';
  const kind = e.kind.toLowerCase();
  return `/docs/${ns}/${kind}/${e.metadata.name}`;
}

// ── Create Documentation Dialog ───────────────────────────────────────────────

interface CreateDocDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateDocDialog = ({ open, onClose, onCreated }: CreateDocDialogProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setTitle('');
    setDescription('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.md$/i, ''));
  };

  const handleSubmit = async () => {
    if (!file || !title.trim()) {
      alertApi.post({ message: 'Title and .md file are required.', severity: 'warning' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.md')) {
      alertApi.post({ message: 'Only .md (Markdown) files are accepted.', severity: 'warning' });
      return;
    }

    setUploading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', title.trim());
      formData.append('description', description.trim());
      formData.append('artifact_type', 'document');
      formData.append('category', DOCS_INSTANCE_CATEGORY);

      const resp = await fetch(`${baseUrl}/artifacts`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error(err.error ?? 'Upload failed');
      }

      alertApi.post({ message: `"${title.trim()}" uploaded successfully.`, severity: 'success' });
      reset();
      onCreated();
      onClose();
    } catch (err: any) {
      alertApi.post({ message: `Failed: ${err.message}`, severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Documentation</DialogTitle>
      <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
        {uploading && <LinearProgress />}

        <TextField
          label="Title *"
          value={title}
          onChange={e => setTitle(e.target.value)}
          disabled={uploading}
          variant="outlined"
          size="small"
          fullWidth
          autoFocus
        />

        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={uploading}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={2}
        />

        <Button
          variant="outlined"
          component="label"
          disabled={uploading}
          startIcon={<DescriptionIcon />}
        >
          {file ? file.name : 'Upload .md File *'}
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,text/markdown"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        {file && (
          <Typography variant="caption" color="textSecondary">
            {formatBytes(file.size)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={uploading || !file || !title.trim()}>
          {uploading ? 'Uploading…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Documentation Tab ─────────────────────────────────────────────────────────

const DocumentationTab = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const [docEntities, setDocEntities] = useState<CatalogEntity[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocArtifact[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [docsLoading, setDocsLoading] = useState(true);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);

  const fetchUploadedDocs = useCallback(async () => {
    setDocsLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(`${baseUrl}/artifacts?type=document`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const data = await resp.json();
      const all: UploadedDocArtifact[] = data.artifacts ?? data ?? [];
      setUploadedDocs(all.filter(d => (d as any).category === DOCS_INSTANCE_CATEGORY));
    } catch {
      setUploadedDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [discoveryApi, identityApi]);

  // ── Fetch catalog entities with spec.type=documentation ──────────────────
  const fetchEntities = useCallback(async () => {
    setEntitiesLoading(true);
    try {
      const resp = await catalogApi.getEntities({
        fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'metadata.description', 'metadata.annotations', 'metadata.tags', 'kind', 'spec.type', 'spec.lifecycle', 'spec.owner'],
        filter: [{ kind: 'Component', 'spec.type': 'documentation' }],
      });
      setDocEntities(resp.items as CatalogEntity[]);
    } catch {
      setDocEntities([]);
    } finally {
      setEntitiesLoading(false);
    }
  }, [catalogApi]);

  const refreshDocsData = useCallback(async () => {
    await Promise.all([fetchEntities(), fetchUploadedDocs()]);
  }, [fetchEntities, fetchUploadedDocs]);

  const deleteUploadedDoc = useCallback(
    async (docId: string, docName: string) => {
      if (!window.confirm(`Delete "${docName}"?`)) return;

      setDeletingDocId(docId);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('persistence');
        const { token } = await identityApi.getCredentials();
        const resp = await fetch(`${baseUrl}/artifacts/${docId}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (resp.status === 404) {
          // The artifact may already be gone while the catalog entity is still
          // visible briefly due catalog refresh lag.
          alertApi.post({ message: `"${docName}" was already deleted.`, severity: 'info' });
          await refreshDocsData();
          return;
        }

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ error: resp.statusText }));
          throw new Error(err.error ?? 'Delete failed');
        }

        alertApi.post({ message: `Deleted "${docName}"`, severity: 'success' });
        await refreshDocsData();
      } catch (err: any) {
        alertApi.post({ message: `Delete failed: ${err.message}`, severity: 'error' });
      } finally {
        setDeletingDocId(null);
      }
    },
    [alertApi, discoveryApi, identityApi, refreshDocsData],
  );

  const openUploadedDoc = useCallback(
    async (doc: OpenUploadedDocInput) => {
      navigate(`/docs/instance/${doc.id}`);
    },
    [navigate],
  );

  useEffect(() => { refreshDocsData(); }, [refreshDocsData]);

  const uploadedDocIds = new Set(uploadedDocs.map(d => d.id));

  const visibleDocEntities = docEntities.filter(entity => {
    const tags = entity.metadata.tags ?? [];
    const artifactId = entity.metadata.annotations?.['code4ev.io/artifact-id'];
    const isUploadedDocsEntity =
      tags.includes('docs-instance') || entity.metadata.name.startsWith('docs-instance-');

    // Hide stale uploaded-doc entities whose source artifact was already
    // deleted, so the docs list reflects actual persisted uploads.
    if (isUploadedDocsEntity && artifactId && !uploadedDocIds.has(artifactId)) {
      return false;
    }

    return true;
  });

  const artifactIdsInCatalog = new Set(
    visibleDocEntities
      .map(e => e.metadata.annotations?.['code4ev.io/artifact-id'])
      .filter((v): v is string => Boolean(v)),
  );

  const pendingUploadedDocs = uploadedDocs.filter(d => !artifactIdsInCatalog.has(d.id));

  return (
    <>
      {/* ── Create Documentation CTA ── */}
      <div className={classes.createCard}>
        <FolderIcon style={{ fontSize: 40, color: '#007EB1' }} />
        <div className={classes.createCardText}>
          <p className={classes.createCardTitle}>Create Documentation</p>
          <p className={classes.createCardSub}>
            Upload a Markdown (.md) file. It will appear in Documentation Repositories.
          </p>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create
        </Button>
      </div>

      {/* ── Documentation repos from catalog ── */}
      <p className={classes.sectionLabel}>Documentation Repositories</p>

      {entitiesLoading || docsLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <CircularProgress size={28} />
        </div>
      ) : visibleDocEntities.length === 0 && pendingUploadedDocs.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No documentation-type repositories found in the catalog.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {pendingUploadedDocs.map(doc => (
            <Grid item xs={12} sm={6} md={4} key={doc.id}>
              <div className={classes.entityCard} style={{ borderLeftColor: '#007EB1' }}>
                <p className={classes.entityCardTitle}>{doc.name}</p>
                {doc.description && <p className={classes.entityCardDesc}>{doc.description}</p>}
                <div className={classes.entityCardFooter}>
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      openUploadedDoc(doc);
                    }}
                    style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}
                  >
                    <OpenInNewIcon style={{ fontSize: 14 }} />
                    Open Docs
                  </a>
                  <Button
                    size="small"
                    color="secondary"
                    disabled={deletingDocId === doc.id}
                    onClick={() => deleteUploadedDoc(doc.id, doc.name)}
                    style={{ minWidth: 0, padding: '2px 6px', fontSize: 11 }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Grid>
          ))}

          {visibleDocEntities.map(entity => {
            const tags = entity.metadata.tags ?? [];
            const lifecycle = entity.spec?.lifecycle ?? '';
            const artifactId = entity.metadata.annotations?.['code4ev.io/artifact-id'];
            const isUploadedDocsEntity =
              tags.includes('docs-instance') || entity.metadata.name.startsWith('docs-instance-');
            return (
              <Grid item xs={12} sm={6} md={4} key={entity.metadata.name}>
                <div className={classes.entityCard} style={{ borderLeftColor: '#007EB1' }}>
                  <p className={classes.entityCardTitle}>
                    {entity.metadata.title ?? entity.metadata.name}
                  </p>
                  {entity.metadata.description && (
                    <p className={classes.entityCardDesc}>{entity.metadata.description}</p>
                  )}
                  {lifecycle && (
                    <p className={classes.entityCardDesc} style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}>
                      Lifecycle: {lifecycle}
                    </p>
                  )}
                  <div className={classes.entityCardFooter}>
                    <Link to={getEntityCatalogPath(entity)} style={{ fontSize: 12, fontWeight: 600 }}>
                      View Entity
                    </Link>
                    {artifactId && isUploadedDocsEntity ? (
                      <a
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          openUploadedDoc({
                            id: artifactId,
                            name: entity.metadata.title ?? entity.metadata.name,
                          });
                        }}
                        style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}
                      >
                        <OpenInNewIcon style={{ fontSize: 14 }} />
                        Open Docs
                      </a>
                    ) : (
                      <Link
                        to={getEntityDocsPath(entity)}
                        style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}
                      >
                        <OpenInNewIcon style={{ fontSize: 14 }} />
                        Open Docs
                      </Link>
                    )}
                    {artifactId && (
                      <Button
                        size="small"
                        color="secondary"
                        disabled={deletingDocId === artifactId}
                        onClick={() => deleteUploadedDoc(artifactId, entity.metadata.title ?? entity.metadata.name)}
                        style={{ minWidth: 0, padding: '2px 6px', fontSize: 11 }}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialogs ── */}
      <CreateDocDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refreshDocsData}
      />
    </>
  );
};

// ── Repository Tab ────────────────────────────────────────────────────────────

const RepositoryTab = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const gitlabBuilder = useApi(GitlabCIApiRef);
  const configApi = useApi(configApiRef);

  const GITLAB_INSTANCE =
    configApi.getOptionalConfigArray('integrations.gitlab')?.[0]?.getOptionalString('host') ??
    'gitlab.tu-ilmenau.de';

  const [entities, setEntities] = useState<CatalogEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState<Record<string, PipelineInfo | null>>({});
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());

  const GITLAB_SLUG_ANNOTATION = 'gitlab.com/project-slug';

  const getSlug = (e: CatalogEntity) =>
    e.metadata.annotations?.[GITLAB_SLUG_ANNOTATION];

  // ── Fetch non-documentation catalog entities ──────────────────────────────
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    catalogApi
      .getEntities({
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'metadata.annotations',
          'metadata.tags',
          'kind',
          'spec.type',
          'spec.lifecycle',
          'spec.owner',
        ],
        filter: [{ kind: 'Component' }],
      })
      .then(resp => {
        if (!mounted) return;
        const items = (resp.items as CatalogEntity[]).filter(
          e =>
            e.spec?.type !== 'documentation' &&
            getSlug(e) !== undefined,
        );
        setEntities(items);
      })
      .catch(() => { if (mounted) setEntities([]); })
      .finally(() => { if (mounted) setLoading(false); });

    return () => { mounted = false; };
  }, [catalogApi]);

  // ── Fetch pipeline statuses ───────────────────────────────────────────────
  useEffect(() => {
    if (entities.length === 0) return;
    const gitlabApi = gitlabBuilder.build(GITLAB_INSTANCE);

    Promise.allSettled(
      entities.map(async entity => {
        const slug = getSlug(entity)!;
        try {
          const result = await gitlabApi.getPipelineSummary(encodeURIComponent(slug));
          return { name: entity.metadata.name, pipeline: (result?.[0] ?? null) as PipelineInfo | null, unavailable: false };
        } catch {
          return { name: entity.metadata.name, pipeline: null, unavailable: true };
        }
      }),
    ).then(results => {
      const map: Record<string, PipelineInfo | null> = {};
      const unavailSet = new Set<string>();
      results.forEach(r => {
        if (r.status === 'fulfilled') {
          map[r.value.name] = r.value.pipeline;
          if (r.value.unavailable) unavailSet.add(r.value.name);
        }
      });
      setPipelines(map);
      setUnavailable(unavailSet);
    });
  }, [entities, gitlabBuilder, GITLAB_INSTANCE]);

  // ── Derived type groupings ────────────────────────────────────────────────
  const typeGroups = entities.reduce<Record<string, CatalogEntity[]>>((acc, e) => {
    const t = e.spec?.type ?? 'other';
    if (!acc[t]) acc[t] = [];
    acc[t].push(e);
    return acc;
  }, {});

  const TYPE_COLORS: Record<string, string> = {
    service: '#007EB1',
    'ai-model': '#6B21A8',
    firmware: '#065F46',
    sdk: '#92400E',
    infrastructure: '#374151',
    'data-schema': '#B45309',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
        <CircularProgress size={36} />
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No repositories found in the catalog.
      </Typography>
    );
  }

  return (
    <>
      {Object.entries(typeGroups).map(([type, items]) => (
        <div key={type} style={{ marginBottom: 28 }}>
          <p className={classes.sectionLabel}>{type.replace(/-/g, ' ')}</p>
          <Grid container spacing={2}>
            {items.map(entity => {
              const pipeline = pipelines[entity.metadata.name];
              const isUnavail = unavailable.has(entity.metadata.name);
              const pStatus = pipeline?.status ?? 'unknown';
              const borderColor = isUnavail ? '#6b7280' : getPipelineColor(pStatus);
              const typeColor = TYPE_COLORS[type] ?? '#007EB1';
              const lifecycle = entity.spec?.lifecycle;

              return (
                <Grid item xs={12} sm={6} md={4} key={entity.metadata.name}>
                  <Link to={getEntityCatalogPath(entity)} underline="none">
                    <div className={classes.entityCard} style={{ borderLeftColor: borderColor }}>
                      <p className={classes.entityCardTitle}>
                        {entity.metadata.title ?? entity.metadata.name}
                      </p>
                      {entity.metadata.description && (
                        <p className={classes.entityCardDesc}>{entity.metadata.description}</p>
                      )}
                      <div className={classes.entityCardFooter}>
                        <Chip
                          label={type}
                          size="small"
                          className={classes.chip}
                          style={{ background: typeColor + '1A', color: typeColor }}
                        />
                        {lifecycle && (
                          <Chip
                            label={lifecycle}
                            size="small"
                            className={classes.chip}
                            style={{ background: '#F0F0F0', color: '#555' }}
                          />
                        )}
                        {!isUnavail && pipeline && (
                          <span style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                            <span
                              className={classes.statusDot}
                              style={{ background: getPipelineColor(pStatus) }}
                            />
                            <Typography variant="caption" style={{ color: getPipelineColor(pStatus) }}>
                              {pStatus}
                            </Typography>
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </div>
      ))}
    </>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const DocsInstancePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const classes = useStyles();

  return (
    <Page themeId="documentation">
      <Header
        title="Documentation"
        subtitle="Browse documentation repositories and manage technical markdown documents"
      />
      <Content>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsBar}
        >
          <Tab label="Documentation" />
          <Tab label="Repository" />
        </Tabs>

        {activeTab === 0 && <DocumentationTab />}
        {activeTab === 1 && <RepositoryTab />}
      </Content>
    </Page>
  );
};
